import { useState } from "react";
import { Link } from "react-router-dom";
import { api, BACKEND_URL } from "@/lib/api";
import useMyRefCode from "@/hooks/useMyRefCode";
import { Check, Loader2, Twitter, Copy, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import Confetti from "@/pages/payment/Confetti";

function ShareCardBlock({ coinSlug, coinName, coinUrl, tweetText }) {
  return (
    <div className="max-w-xl mx-auto mb-6 text-left" data-testid="instant-share-card">
      <div className="font-mono text-[11px] uppercase tracking-widest text-[#cfff04] mb-2">
        // Your share card is ready
      </div>
      <div className="border border-[#3a3a3a] bg-[#121212] p-3">
        <img
          src={`${BACKEND_URL}/api/coins/${coinSlug}/card.png?ts=${Date.now()}`}
          alt={`${coinName || coinSlug} share card`}
          data-testid="instant-share-card-img"
          className="w-full border border-[#3a3a3a]"
          onError={(e) => { e.currentTarget.closest('[data-testid="instant-share-card"]').style.display = "none"; }}
        />
        <div className="flex flex-wrap items-center gap-3 mt-3">
          <a
            href={`${BACKEND_URL}/api/coins/${coinSlug}/card.png`}
            download={`${coinSlug}-card.png`}
            data-testid="instant-card-download"
            className="font-mono text-[11px] uppercase tracking-widest text-[#cfff04] underline hover:no-underline"
          >
            Download card ↓
          </a>
          <a
            href={`https://t.me/share/url?url=${encodeURIComponent(coinUrl)}&text=${encodeURIComponent(tweetText)}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="instant-card-telegram"
            className="font-mono text-[11px] uppercase tracking-widest text-[#e2e2e2] underline hover:text-white hover:no-underline"
          >
            Share on Telegram →
          </a>
        </div>
      </div>
    </div>
  );
}

function KitUpsell({ coinSlug, coinName }) {
  const [kitBusy, setKitBusy] = useState(false);
  const buyKit = async () => {
    if (!coinSlug) return;
    setKitBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        sku: "LAUNCH_KIT",
        coin_slug: coinSlug,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      setKitBusy(false);
      toast.error(e.response?.data?.detail || "Checkout failed — you can grab the kit from your coin page");
    }
  };
  return (
    <div className="border-2 border-[#cfff04] bg-[#cfff04]/5 p-5 max-w-xl mx-auto text-left mb-4" data-testid="kit-upsell">
      <div className="font-mono text-[11px] uppercase tracking-widest text-[#cfff04] mb-2">
        // Strike while you're hot
      </div>
      <p className="text-sm text-[#e2e2e2] mb-3">
        Add the <span className="text-white font-bold">AI Launch Kit</span> — a ready-to-post
        tweet thread, promo banner, and memes written for {coinName || "your coin"} in ~30 seconds.
      </p>
      <button
        onClick={buyKit}
        disabled={kitBusy}
        data-testid="kit-upsell-buy"
        className="btn-brutal inline-flex bg-[#cfff04] text-[#050505] font-bold uppercase tracking-wider text-xs px-5 py-3 items-center gap-2 disabled:opacity-50"
      >
        {kitBusy ? <Loader2 size={14} className="animate-spin" /> : null}
        Add Launch Kit — $9 →
      </button>
    </div>
  );
}

function NextStepCta({ sku, coinSlug, coinName }) {
  if (sku === "LAUNCH_KIT") {
    return (
      <div className="border border-[#00ff41]/60 bg-[#00ff41]/5 p-5 max-w-xl mx-auto text-left mb-4" data-testid="kit-cta">
        <div className="font-mono text-[11px] uppercase tracking-widest text-[#00ff41] mb-2">
          // Your kit is waiting
        </div>
        <p className="text-sm text-[#e2e2e2] mb-3">
          Head to your coin page and hit <span className="text-white">"Generate my Launch Kit"</span> —
          the AI writes everything in ~30 seconds.
        </p>
        <Link
          to={`/c/${coinSlug}`}
          data-testid="kit-cta-link"
          className="btn-brutal inline-flex bg-[#00ff41] text-[#050505] font-bold uppercase tracking-wider text-xs px-5 py-3"
        >
          Generate my kit →
        </Link>
      </div>
    );
  }
  return (
    <div className="border border-[#00ff41]/60 bg-[#00ff41]/5 p-5 max-w-xl mx-auto text-left mb-4" data-testid="mint-cta">
      <div className="font-mono text-[11px] uppercase tracking-widest text-[#00ff41] mb-2">
        // Unlocked · Make it a real token
      </div>
      <p className="text-sm text-[#e2e2e2] mb-3">
        Publishing unlocked <span className="text-white">one-click on-chain minting</span> —
        turn {coinName || "your coin"} into an actual Solana token with a single Phantom
        approval. Free practice on devnet first.
      </p>
      <Link
        to={`/c/${coinSlug}#mint`}
        data-testid="mint-cta-link"
        className="btn-brutal inline-flex bg-[#00ff41] text-[#050505] font-bold uppercase tracking-wider text-xs px-5 py-3"
      >
        Mint it on-chain →
      </Link>
    </div>
  );
}

/** The full "you're live" celebration screen shown once payment is confirmed. */
export default function PaidCelebration({ coin, sku }) {
  const { slug: coinSlug, name: coinName, ticker: coinTicker } = coin;
  const refCode = useMyRefCode();
  const coinUrl = coinSlug
    ? `${window.location.origin}/c/${coinSlug}${refCode ? `?ref=${refCode}` : ""}`
    : "";
  const tweetText = coinName
    ? `I just launched ${coinName}${coinTicker ? ` ($${coinTicker})` : ""} on @solspurr — AI-generated coin in 60 seconds. Check it out 👇`
    : "I just launched a coin on SolSpur!";

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(coinUrl);
      toast.success("Link copied!");
    } catch {
      toast.error("Copy failed — long-press to copy manually");
    }
  };

  return (
    <>
      <Confetti />
      <div className="text-center" data-testid="payment-paid">
        <div className="w-20 h-20 bg-[#cfff04] mx-auto mb-6 flex items-center justify-center animate-pulse">
          <Check size={42} className="text-[#050505]" strokeWidth={3} />
        </div>
        <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#00ff41] mb-3">
          // Payment confirmed · You&apos;re a paid creator
        </div>
        <h1 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tighter mb-3">
          {sku === "LAUNCH_KIT" ? (
            <>Launch Kit <span className="text-[#cfff04]">unlocked</span>.</>
          ) : coinName ? (
            <>{coinName} is <span className="text-[#cfff04]">live</span>.</>
          ) : (
            <>Your coin is live.</>
          )}
        </h1>
        <p className="text-[#e2e2e2] mb-8">
          {sku === "LAUNCH_KIT"
            ? "AI is ready to write your tweet thread, promo banner, and pitches — one click on your coin page."
            : "Share it now while you're hot. Every visitor = potential tip in SOL → straight to your wallet."}
        </p>

        {coinSlug && (
          <ShareCardBlock coinSlug={coinSlug} coinName={coinName} coinUrl={coinUrl} tweetText={tweetText} />
        )}

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8 max-w-xl mx-auto">
          <a
            href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}&url=${encodeURIComponent(coinUrl)}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="share-twitter"
            className="btn-brutal bg-[#1d9bf0] text-white font-bold uppercase tracking-wider text-sm px-4 py-3 inline-flex items-center justify-center gap-2"
          >
            <Twitter size={16} /> Tweet
          </a>
          <button
            onClick={copyLink}
            data-testid="share-copy"
            className="btn-brutal bg-[#cfff04] text-[#050505] font-bold uppercase tracking-wider text-sm px-4 py-3 inline-flex items-center justify-center gap-2"
          >
            <Copy size={16} /> Copy Link
          </button>
          <Link
            to={`/c/${coinSlug}`}
            data-testid="goto-coin"
            className="btn-brutal border border-[#cfff04] text-[#cfff04] font-bold uppercase tracking-wider text-sm px-4 py-3 inline-flex items-center justify-center gap-2"
          >
            <ExternalLink size={16} /> View Coin
          </Link>
        </div>

        {sku !== "LAUNCH_KIT" && <KitUpsell coinSlug={coinSlug} coinName={coinName} />}
        <NextStepCta sku={sku} coinSlug={coinSlug} coinName={coinName} />

        <div className="border border-[#3a3a3a] bg-[#121212] p-5 max-w-xl mx-auto text-left">
          <div className="font-mono text-[11px] uppercase tracking-widest text-[#cfff04] mb-2">
            // Next moves for max reach
          </div>
          <ul className="text-sm text-[#e2e2e2] space-y-1.5">
            <li>1. Tweet your coin link with the AI-generated image</li>
            <li>2. Post in r/CryptoCurrency or r/solana with &ldquo;I built this on SolSpur&rdquo;</li>
            <li>3. Drop in 2-3 Telegram crypto chats</li>
            <li>4. DM 5 meme-coin influencers offering free launches via your referral link</li>
          </ul>
        </div>
      </div>
    </>
  );
}
